import styled from 'styled-components/native';
import { useTheme } from 'styled-components';
import { Theme } from '../../../theme/lightTheme';
import React, { useCallback, useState } from 'react';
import Animated, { FadeInDown, FadeOutUp } from 'react-native-reanimated';
import { tr } from '../../../translations';
import {
  RefreshControl,
  StatusBar,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from '../../icon';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import TextGeneric from '../../textGeneric';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { EmptyChatProps } from '../../../interfaces/components/emptyChat';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import ChatFooter from '../chatFooter';
import { useCustomHeaderHeight } from '../../../utils/hooks/useHeaderHeight';
import CircleRecordingView from '../../circleRecordingView';
import { ChatCategory } from '../../../interfaces/api/auth/login';
import EmptyList from './../../../components/emptyList';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { Platform } from 'react-native';
import '../../../utils/webAnimations';


const AnimatedTouchableOpacity =
  Animated.createAnimatedComponent(TouchableOpacity);


const Container = styled(Animated.ScrollView)<{
  cutHeight?: number;
  width?: number;
  top?: number;
}>`
  align-self: center;
  position: absolute;
  top: ${({ top }) => top ?? 0}px;
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  height: 100%;
  z-index: 99;
  border-radius: 16px;
  overflow: hidden;
`;

const ContentContainer = styled(Animated.View)<{ isLargeScreen?: boolean }>`
  align-self: center;
  width: 100%;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  z-index: 99;
  shadow-color: ${({ theme }) => theme.text.black};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  padding: ${({ isLargeScreen }) => isLargeScreen ? '2% 0' : '5% 0'};
`;

const QnaContentContainer = styled(Animated.View)<{ headerHeight: number; isLargeScreen?: boolean }>`
  width: 100%;
  height: 100%;
  padding-bottom: ${({ isLargeScreen }) => isLargeScreen ? '2%' : '5%'};
  padding-top: ${({ headerHeight, isLargeScreen }) => isLargeScreen ? headerHeight * 0.7 : headerHeight}px;
  align-items: center;
  justify-content: space-between;
`;

const WelcomeQNAText = styled(TextGeneric)`
  color: ${({ theme }) => theme.text.dark};
  font-size: ${({ theme }) => theme.text.s3}px;
  text-align: left;
  padding-top: ${({ theme }) => theme.text.s3}px;
  width: 90%;
`;

const BottomQNAText = styled(TextGeneric)`
  color: ${({ theme }) => theme.text.dark};
  font-size: ${({ theme }) => theme.text.s7}px;
  text-align: center;
  width: 50%;
`;

const QuestionsContainer = styled.View`
  width: 100%;
  align-self: center;
`;

const QuestionContainer = styled(AnimatedTouchableOpacity)<{ isSelected: boolean; isLargeScreen?: boolean }>`
  width: 100%;
  align-self: center;
  margin-top: ${({ isLargeScreen }) => isLargeScreen ? '1%' : '2%'};
  background-color: ${({ theme, isSelected }) =>
    isSelected ? theme.cardBackgroundEnd : theme.cardBackground};
  padding: ${({ isLargeScreen }) => isLargeScreen ? '2% 3%' : '3% 4%'};
  border-radius: 8px;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;

const QuestionTitle = styled(TextGeneric)`
  color: ${({ theme, disabled }) =>
    disabled ? theme.text.dark + 'BB' : theme.text.dark};
  font-size: ${({ theme }) => theme.text.s7}px;
  flex: 1;
  padding-right: 5%;
`;

const IconContainer = styled.View`
  align-items: center;
  justify-content: center;
`;

const QuestionsTitle = styled(TextGeneric)<{ isLargeScreen?: boolean }>`
  color: ${({ theme }) => theme.text.dark};
  font-size: ${({ theme }) => theme.text.s6}px;
  width: 100%;
  padding-bottom: ${({ isLargeScreen }) => isLargeScreen ? '2%' : '5%'};
`;

/**
 * Wrapper used when the list is empty or loading.
 */
const EmptyContainer = styled(View)`
  width: 100%;
  align-items: center;
  justify-content: center;
`;

/**
 * Skeleton placeholder shown while questions are loading.
 * (You can replace this with your own skeleton component.)
 */
const SearchListSkeleton = styled(View)`
  width: 90%;
  height: 200px;
  background-color: ${({ theme }) => theme.cardBackground};
  border-radius: 12px;
`;

/* ------------------------------------------------------------------ */
/*  Component                                                            */
/* ------------------------------------------------------------------ */
const EmptyChat: React.FC<EmptyChatProps> = ({
  style,
  introQuestions,
  isIntroQuestionsLoading,
  isIntroQuestionsError,
  onIntroQuestionsRefreshPress,
  isQNA,
  onQuestionPress,
  userName,
  onCheckAnswerPressed,
  question,
  setQuestion,
  onRecordHandler,
  isRecording,
}: EmptyChatProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const isFocused = useIsFocused();
  const [selected, setSelected] = useState<any>();
  const { top: topInset, bottom: bottomInset } = useSafeAreaInsets();
  const headerHeight = useCustomHeaderHeight() || 100;
  const {selectedCase} = useSelector((state: RootState) => state.chat);

  const { wdp, hdp, getByScreenSize, isLargeScreen } = useResponsiveDimensions();

  /* Reset selected question when screen loses focus */
  useFocusEffect(
    useCallback(() => {
      return () => setSelected(undefined);
    }, [isQNA]),
  );

  const _renderQuestion = (item: ChatCategory, index: number) => (
    <QuestionContainer
      isSelected={selected?.id === item.id}
      isLargeScreen={isLargeScreen}
      key={index}
      activeOpacity={0.8}
      style={{opacity: !selectedCase?.trim() ? 0.8 : 1}}
      disabled={!selectedCase?.trim()}
      onPress={() => {
        if (!selectedCase?.trim()) return;
        setSelected(item);
        onQuestionPress?.(item);
        onCheckAnswerPressed?.(item);
      }}
      {...(Platform.OS === 'web'
        ? {className: 'screen-fade-in-down'}
        : {entering: FadeInDown.delay(index * 100)})}
    >
      <QuestionTitle>{item.title ?? ''}</QuestionTitle>
      <IconContainer>
        <Icon
          name="navArrow"
          size={theme.text.s4}
          color={`${theme.secondary}11`}
          stroke={theme.secondary}
        />
      </IconContainer>
    </QuestionContainer>
  );

  if (!isFocused) return <></>;

  /* Q&A or selected question view */
  if (isQNA || selected) {
    return (
      <QnaContentContainer headerHeight={headerHeight} isLargeScreen={isLargeScreen}>
        <WelcomeQNAText>
          {isQNA && (
            <WelcomeQNAText
              bold
              style={{ fontSize: theme.text.s6 }}
            >
              {tr('voiceInteraction.welcomeQNAPart1') + '\n\n'}
            </WelcomeQNAText>
          )}
          {isQNA ? (
            <>
              {tr('voiceInteraction.welcomeQNAPart2')}{' '}
              <WelcomeQNAText bold>{userName || 'User'}</WelcomeQNAText>{' '}
              {tr('voiceInteraction.welcomeQNAPart3')}
            </>
          ) : (
            tr('voiceInteraction.welcomeClinicalCase')
          )}
        </WelcomeQNAText>
        <CircleRecordingView
          isRecording={!!isRecording}
          style={{ width: getByScreenSize('60%', '25%') }}
        />
        <BottomQNAText bold>
          {isQNA
            ? tr('voiceInteraction.footerQNA')
            : tr('voiceInteraction.footerClinicalCase')}
        </BottomQNAText>
      </QnaContentContainer>
    );
  }

  /* Default chat view */
  return (
    <Container
      width={undefined}
      top={hdp(1)}
      contentContainerStyle={{
        padding: '2%',
        paddingTop: headerHeight,
      }}
      indicatorStyle="black"
      refreshControl={
        <RefreshControl
          tintColor={theme.secondary}
          refreshing={!!isIntroQuestionsLoading && !introQuestions?.length}
          onRefresh={onIntroQuestionsRefreshPress}
        />
      }
      showsVerticalScrollIndicator={false}
      cutHeight={
        headerHeight + topInset + bottomInset + (StatusBar.currentHeight ?? 0)
      }
      {...(Platform.OS === 'web'
        ? {} // Don't animate container on web - let items animate instead
        : {entering: FadeInDown, exiting: FadeOutUp})}
      style={style}
    >
      <ContentContainer isLargeScreen={isLargeScreen}>
        <QuestionsContainer>
          <QuestionsTitle semiBold isLargeScreen={isLargeScreen}>
            Click below to receive evidence based guidance{' '}
            <QuestionsTitle style={{ color: theme.text.error }} bold>
              *
            </QuestionsTitle>
          </QuestionsTitle>

          {isIntroQuestionsLoading ? (
            <EmptyContainer
              style={{
                minHeight: isLargeScreen ? hdp(20) : hdp(30),
                top: isLargeScreen ? '2%' : '5%',
              }}
            >
              <SearchListSkeleton />
            </EmptyContainer>
          ) : isIntroQuestionsError ? (
            <EmptyContainer
              style={{
                minHeight: isLargeScreen ? hdp(20) : hdp(30),
                top: isLargeScreen ? '2%' : '5%',
              }}
            >
              <EmptyList
                iconName="alert"
                iconColor={theme.primary}
                iconSize={theme.text.s1 * 2.5}
                onRefreshPress={onIntroQuestionsRefreshPress}
                title="app.error"
                titleStyle={{ color: theme.text.black }}
                showRefresh
              />
            </EmptyContainer>
          ) : (
            (introQuestions ?? []).map(_renderQuestion)
          )}
        </QuestionsContainer>
      </ContentContainer>

      <View style={{ height: headerHeight }} />
    </Container>
  );
};

export default EmptyChat;
